/* eslint-disable complexity */
import React, { useEffect, useState } from 'react';
import {
  OnboardingFlowNodeType,
  OnboardingFlowNode,
  OnboardingFlowStepNode,
  OnboardingFlowTransition,
  StepStatsResponse,
  OnboardingStepType,
  RESUME_SCREEN_FAILED_NODE_NAME,
  RESUME_SCREEN_MANUAL_REVIEW_NODE_NAME,
} from '@scale/shared/onboardingControlPanel';
import { twMerge } from 'tailwind-merge';
import { Icon, OptionsMenu, Tabs, Typography, MenuItemProps } from '@scale/scaleui';
import { useOCPStore } from '../../hooks/useOCPStore';
import { useDraftFlowStore } from '../../hooks/useDraftFlowStore';
import FlowEntityConfig, { FlowEntityType } from '../../config/FlowEntityConfig';
import { isNodeUneditable, isTransitionUneditable } from '../../config/config';
import { NameInput } from '../ConfigComponents';
import DetailPanelNodeView from './DetailPanelNodeView';
import DetailPanelTransitionView from './DetailPanelTransitionView';
import DetailPanelFlowOverview from './DetailPanelFlowOverview';
import PanelEntry from '../PanelEntry';
import { getStepBreakdownStats } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/api';
import { getCampaignForOcpNode } from 'react-scale/corp/lib/api/growth';
import { Routes } from 'react-scale/components/GrowthCenter/urls';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { Variant } from '@scale/scaleui/dist/v1/types';
import { RescreenUsersModal } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/RescreenUsersModal';

function isTargetTransition(
  target: OnboardingFlowNode | OnboardingFlowTransition | null,
): target is OnboardingFlowTransition {
  return target !== null && (target as OnboardingFlowTransition)?.sourceNode !== undefined;
}

const overviewTabOptions = [{ id: 'Metrics' }, { id: 'Flow Details' }];

const DetailPanel = () => {
  const {
    detailPanelOpen,
    toggleDetailPanel,
    detailTarget,
    currentVersion,
    setCurrentVersion,
    currentFlow,
    validationResults,
    setDetailTarget,
    flowFilterTags,
    canFreeEdit,
  } = useOCPStore(state => ({
    detailPanelOpen: state.detailPanelOpen,
    toggleDetailPanel: state.toggleDetailPanel,
    detailTarget: state.detailTarget,
    currentVersion: state.currentVersion,
    setCurrentVersion: state.setCurrentVersion,
    currentFlow: state.currentFlow,
    validationResults: state.validationResults,
    setDetailTarget: state.setDetailTarget,
    flowFilterTags: state.flowFilterTags,
    flowFilterDates: state.flowFilterDates,
    canFreeEdit: state.canFreeEdit,
  }));
  const { deleteDraftEntity, draftFlows } = useDraftFlowStore(state => ({
    deleteDraftEntity: state.deleteDraftEntity,
    draftFlows: state.draftFlows,
  }));

  const tabOptions = [
    { id: 'View' },
    {
      id:
        !!detailTarget &&
        !isTargetTransition(detailTarget) &&
        (detailTarget as OnboardingFlowNode).type === OnboardingFlowNodeType.Step &&
        (detailTarget as OnboardingFlowStepNode).stepType === OnboardingStepType.Signup
          ? 'Add/Edit Signup Flows'
          : 'Edit',
    },
  ];

  const [currentTab, setCurrentTab] = useState<string>(
    detailTarget === null ? overviewTabOptions[0].id : tabOptions[0].id,
  );
  const [rescreenModalOpen, setRescreenModalOpen] = useState<boolean>(false);
  const showRescreenButton =
    detailTarget?.name === RESUME_SCREEN_FAILED_NODE_NAME ||
    detailTarget?.name === RESUME_SCREEN_MANUAL_REVIEW_NODE_NAME;
  const nonDraftMode = currentVersion !== -1;
  const uneditable =
    isNodeUneditable(detailTarget as OnboardingFlowNode, canFreeEdit ? currentFlow : null) ||
    isTransitionUneditable(
      detailTarget as OnboardingFlowTransition,
      canFreeEdit ? currentFlow : null,
    );

  useEffect(() => {
    if (detailTarget === null && !overviewTabOptions.map(o => o.id).includes(currentTab))
      setCurrentTab(overviewTabOptions[0].id);
    else if (detailTarget !== null && overviewTabOptions.map(o => o.id).includes(currentTab))
      setCurrentTab(tabOptions[0].id);
  }, [detailTarget, currentTab]);

  useEffect(() => {
    if (currentTab.includes('Edit') && nonDraftMode) {
      setCurrentVersion(-1);
    }
  }, [currentTab, setCurrentVersion, nonDraftMode]);

  const configComponents: React.ReactNode[] = detailTarget
    ? FlowEntityConfig[
        isTargetTransition(detailTarget)
          ? FlowEntityType.Transition
          : (detailTarget as OnboardingFlowNode).type === OnboardingFlowNodeType.Step
          ? (detailTarget as OnboardingFlowStepNode).stepType
          : (detailTarget as OnboardingFlowNode).type
      ] ?? []
    : [];

  const possibleTransitions = !detailTarget
    ? []
    : !isTargetTransition(detailTarget)
    ? []
    : !nonDraftMode
    ? draftFlows[currentFlow?._id ?? '']?.edges.filter(
        e => e.sourceNode === detailTarget.sourceNode && e.destNode === detailTarget.destNode,
      ) ?? []
    : currentFlow?.edges.filter(
        e => e.sourceNode === detailTarget.sourceNode && e.destNode === detailTarget.destNode,
      ) ?? [];

  const validationErrors = detailTarget
    ? validationResults.errors.filter(error => error.message.includes(detailTarget.name))
    : [];

  const canDelete = !uneditable && !nonDraftMode && detailTarget !== null;
  const shouldLinkToCampaign =
    !nonDraftMode && detailTarget !== null && !isTargetTransition(detailTarget);
  const [loadingStepStats, setLoadingStepStats] = useState(false);

  const [stepStats, setStepStats] = useState<StepStatsResponse>({
    usersEntered24Hours: 0,
    usersExited24Hours: 0,
    usersEntered7Days: 0,
    usersExited7Days: 0,
    usersEnteredAllTime: 0,
    usersExitedAllTime: 0,
  });
  const [emailCampaignId, setEmailCampaignId] = useState<string>('');

  useEffect(() => {
    const fetchStepStats = async () => {
      setLoadingStepStats(true);
      try {
        const tagIds = flowFilterTags.map(tag => tag.value);
        const stats = await getStepBreakdownStats({
          flowId: currentFlow!._id.startsWith('tempId_') ? '' : currentFlow!._id,
          flowVersion: currentVersion,
          step: (detailTarget as OnboardingFlowNode).name,
          tags: tagIds,
        });
        setStepStats(stats);
      } finally {
        setLoadingStepStats(false);
      }
    };
    const fetchCampaign = async () => {
      const campaign = await getCampaignForOcpNode(
        currentFlow!._id,
        (detailTarget as OnboardingFlowNode).name,
      ).catch(() => null);
      if (campaign) {
        setEmailCampaignId(campaign._id.toString());
      } else {
        setEmailCampaignId('');
      }
    };
    if (detailTarget && !isTargetTransition(detailTarget) && currentFlow && currentFlow._id) {
      fetchStepStats();
      fetchCampaign();
    }
  }, [currentFlow, currentFlow?._id, detailTarget, flowFilterTags]);

  const menuItems: MenuItemProps[] = [
    ...(shouldLinkToCampaign
      ? [
          {
            label: emailCampaignId ? 'Manage Campaign' : 'Add Campaign',
            icon: 'envelope' as IconProp,
            onClick: () => {
              window.open(`/corp${Routes.CommunicationSetup}/${emailCampaignId}`, '_blank');
            },
          },
        ]
      : []),
    ...(currentFlow?._id && showRescreenButton
      ? [
          {
            label: 'Rescreen Users',
            icon: 'redo' as IconProp,
            onClick: () => {
              setRescreenModalOpen(true);
            },
          },
        ]
      : []),
    ...(canDelete
      ? [
          {
            label: 'Delete',
            icon: 'trash' as IconProp,
            variant: 'danger' as Variant,
            onClick: () => {
              deleteDraftEntity(currentFlow?._id ?? '', detailTarget);
              setDetailTarget(null);
              toggleDetailPanel();
            },
          },
        ]
      : []),
  ];

  return (
    <>
      {detailPanelOpen && (
        <div
          className={twMerge(
            'scaleui fixed h-full right-0 flex-col flex-grow overflow-y-hidden border-l bg-neutral-0 border-l-neutral-200 transition-all',
            detailPanelOpen ? 'w-[32%]' : 'w-0',
          )}
        >
          <div className="flex items-start w-full">
            <div
              className="flex items-center px-1"
              style={{ width: canDelete ? 'calc(100% - 36px)' : '100%' }}
            >
              {detailTarget === null && currentFlow ? (
                <div className="flex justify-between flex-col w-full">
                  <Typography.H4 className="p-2">Flow Overview</Typography.H4>
                </div>
              ) : uneditable ? (
                <>
                  <Typography.H4 className="p-2 truncate">{detailTarget?.name}</Typography.H4>
                  <Icon rounded variant="neutral" icon="lock" />
                </>
              ) : isTargetTransition(detailTarget) ? (
                <>
                  <div
                    style={{
                      maxWidth: possibleTransitions.length > 1 ? 'calc(100% - 56px)' : '100%',
                    }}
                  >
                    <NameInput />
                  </div>
                  {possibleTransitions.length > 1 && (
                    <OptionsMenu
                      className="mt-1"
                      icon="chevron-down"
                      iconVariant="neutral"
                      direction="bottom-right"
                      menuItems={possibleTransitions.map(t => ({
                        label: t.name,
                        onClick: () => setDetailTarget(t),
                      }))}
                      menuItemsClassName="max-h-[500px] overflow-y-auto"
                    />
                  )}
                  {possibleTransitions.length > 1 && (
                    <Icon
                      icon="info-circle"
                      tooltip="Transitions are evaluated in the order listed, so users will go through the first one satisfied"
                    />
                  )}
                </>
              ) : (
                <NameInput
                  loadingStepStats={loadingStepStats}
                  activeNode={stepStats.usersEnteredAllTime > 0}
                />
              )}
            </div>
            {(canDelete || shouldLinkToCampaign || showRescreenButton) && (
              <div className="ml-auto self-center p-1">
                <OptionsMenu
                  icon="ellipsis-v"
                  size="md"
                  variant="neutral"
                  direction="bottom-left"
                  menuItems={menuItems}
                />
              </div>
            )}
          </div>
          <Tabs
            className="px-2"
            onChange={setCurrentTab}
            tabs={detailTarget === null ? overviewTabOptions : tabOptions}
            value={currentTab}
            variant="default"
          />
          {detailTarget !== null && !isTargetTransition(detailTarget) && (
            <RescreenUsersModal
              isOpen={rescreenModalOpen}
              onClose={() => setRescreenModalOpen(false)}
              stepName={detailTarget!.name}
              flowId={currentFlow!._id}
            />
          )}
          <div className="flex flex-col overflow-y-auto" style={{ height: 'calc(100% - 196px)' }}>
            {!!validationErrors.length && detailTarget !== null && (
              <PanelEntry title="Errors">
                {validationErrors.map((error, index) => (
                  <div key={index} className="flex items-center gap-x-2">
                    <Icon icon="exclamation-circle" variant="danger" />
                    <Typography.Paragraph3 className="text-red-500 m-0 flex-grow">
                      {error.message}
                    </Typography.Paragraph3>
                  </div>
                ))}
              </PanelEntry>
            )}
            {currentTab === 'View' && detailTarget !== null && isTargetTransition(detailTarget) && (
              <DetailPanelTransitionView transition={detailTarget as OnboardingFlowTransition} />
            )}
            {currentTab === 'View' &&
              detailTarget !== null &&
              !isTargetTransition(detailTarget) && (
                <DetailPanelNodeView
                  flowId={currentFlow!._id}
                  flowName={currentFlow!.name}
                  node={detailTarget}
                  stepStats={stepStats}
                  loadingStepStats={loadingStepStats}
                />
              )}

            {currentTab.includes('Edit') && detailTarget !== null && (
              <>
                {configComponents.length ? (
                  configComponents
                ) : (
                  <Typography.Paragraph2 className="p-2">No Editable Configs</Typography.Paragraph2>
                )}
                <PanelEntry title="">
                  <Typography.Paragraph3 className="-mt-2">* required</Typography.Paragraph3>
                </PanelEntry>
              </>
            )}
            {detailTarget === null && currentFlow && (
              <DetailPanelFlowOverview currentFlow={currentFlow} currentTab={currentTab} />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DetailPanel;
